import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { finalize, Observable, Subscription } from 'rxjs';
import { MainService } from '../../../common/services/main.service';
import { FilterMenu } from '../../../common/services/filterMenu.enum';
import { ProjectFilterViewService } from '../../../common-project/services/project-filter-view.service';
import { ProjectFilterView } from '../../../common-project/models/filters/project-filter-view.model';
import { ProjectFilterViewData } from '../../../common-project/models/filters/project-filter-view-data.model';
import { NotificationService } from '../../../core/services/notification.service';
import { LightweightProjectFilterView } from '../../../common-project/models/filters/lightweight-project-filter-view.model';
import { ProjectFilterService } from '../../../common-project/services/project-filter.service';
import { ProjectFilterLookupContainer } from 'src/common-project/models/lookups/project-filter-lookup-container.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'saved-filters',
  templateUrl: './saved-filters.component.html',
  styleUrls: ['./saved-filters.component.scss']
})
export class SavedFiltersComponent implements OnInit, OnDestroy {
  public isSavingNewFilter: boolean = false;
  @Input() public isMobileDisplay: boolean = false;
  public shouldUseScrollableOverflow: boolean = false;
  //saved filters menu visibility
  public isSavedFiltersMenuShowing: boolean = false;
  //has someone selected a saved filter - need to show rest of form
  public isSavedFilterSelected: boolean = true;
  //set up form group, get validation working for the filter name
  private subscriptions = new Array<Subscription>();
  public filterViews: LightweightProjectFilterView[] = [];
  public selectedFilterView: ProjectFilterView = new ProjectFilterView();
  public editableFilter: LightweightProjectFilterView = new LightweightProjectFilterView();
  public filterLookups: ProjectFilterLookupContainer = new ProjectFilterLookupContainer();
  public savedFiltersForm!: FormGroup;

  public readonly FILTER_VIEW_NAME_FORM_CONTROL_NAME = 'filterViewName';
  public readonly FLAG_TYPE_FORM_CONTROL_NAME = 'flagType';
  public readonly DEPARTMENT_FORM_CONTROL_NAME = 'department';
  public readonly OFFICE_FORM_CONTROL_NAME = 'office';
  public readonly OWNER_CLIENT_TYPE_FORM_CONTROL_NAME = 'ownerClientType';
  public readonly PM1_DEPARTMENT_FORM_CONTROL_NAME = 'pm1Department';
  public readonly PM1_OFFICE_FORM_CONTROL_NAME = 'pm1Office';
  public readonly PM1_EMPLOYEE_FORM_CONTROL_NAME = 'pm1Employee';
  public readonly PM2_EMPLOYEE_FORM_CONTROL_NAME = 'pm2Employee';
  public readonly TASK_MANAGER_EMPLOYEE_FORM_CONTROL_NAME = 'taskManagerEmployee';
  public readonly PROJECT_ACCOUNTANT_EMPLOYEE_FORM_CONTROL_NAME = 'projectAccountantEmployee';
  public readonly ACTIVE_STATUS_FORM_CONTROL_NAME = 'isActiveProjectStatus';
  public readonly COMPLETED_STATUS_FORM_CONTROL_NAME = 'isCompletedProjectStatus';
  public readonly DESIGN_BUILD_FORM_CONTROL_NAME = 'isDesignBuild';
  public readonly DEFAULT_FILTER_FORM_CONTROL_NAME = 'isDefault';
  public isFilterViewSaveInProgress: boolean = false;


  constructor(private _mainService: MainService, protected filterViewService: ProjectFilterViewService, private formBuilder: FormBuilder, protected notificationService: NotificationService, protected filterService: ProjectFilterService) { }

  ngOnInit(): void {

    //hide menu when click event happens outside of menu
    const uiSubscription1$ = this._mainService.hideMenu.pipe(finalize(() => {
      this.subscriptions.push(uiSubscription1$);
    })).subscribe(() => {
      //hide menu & send update
      this.setMenuVisibility();
    }, error => {
      console.log(error);
    });
    this.savedFiltersForm = this.formBuilder.group({
      filterViewName: [null, [Validators.required]],
      flagType: [[]],
      office: [[]],
      department: [[]],
      ownerClientType: [[]],
      pm1Department: [[]],
      pm1Office: [[]],
      pm1Employee: [[]],
      pm2Employee: [[]],
      taskManagerEmployee: [[]],
      projectAccountantEmployee: [[]],
      isActiveProjectStatus: [false],
      isCompletedProjectStatus: [false],
      isDesignBuild: [false],
      isDefault: [false]
    });
    const uiSubscription2$ = this._mainService.openMenuName.pipe(finalize(() => {
      this.subscriptions.push(uiSubscription2$);
    })).subscribe((value: FilterMenu) => {
      if (value != FilterMenu.Saved) {
        this.closeMenu(true);
      } else {
        this.closeMenu(false);
      }
    }, error => {
      console.log(error);
    });

    const lookupsLoadedSub = this.filterService.onLookupsLoaded.pipe(finalize(() => {
        this.subscriptions.push(lookupsLoadedSub);
      })).subscribe((result: ProjectFilterLookupContainer) => {
        this.filterLookups = result;
        this.loadFilterViews();
      });


    const filterViewSavedSub = this.filterViewService.onFilterViewSaved.subscribe((savedFilterView: LightweightProjectFilterView) => {
      const existing = this.filterViews.find(x => x.filterViewKey === savedFilterView.filterViewKey);

      if (!existing) {
        this.filterViews.push(savedFilterView);
      } else {
        existing.filterViewName = savedFilterView.filterViewName;
        existing.isDefault = savedFilterView.isDefault;
      }

      this.filterViews.sort((a, b) => (a.filterViewName < b.filterViewName ? -1 : 1));
    });
    this.subscriptions.push(filterViewSavedSub);

    const defaultFilterSub = this.filterViewService.onDefaultFilterViewApplied.subscribe({
      next: (resetAppliedFiltersForNoDefaultFilterView: boolean) => {
        const defaultFilterView = this.filterViews.find(x => x.isDefault);

        if (defaultFilterView?.filterViewKey != null && !this.selectedFilterView.isDefault) {
          this.setSelectedFilterView(defaultFilterView.filterViewKey);
        } else if (this.selectedFilterView != null || resetAppliedFiltersForNoDefaultFilterView) {
          this.selectedFilterView = new ProjectFilterView();
          this.editableFilter = new LightweightProjectFilterView();
          this.filterService.setMyProjectsOnly = false;
          this.filterService.setNeedsReviewedProjects = false;
          this.filterService.setNeedsUpdatedProjects = false;
          this.filterViewService.filterViewApplied$.next(new LightweightProjectFilterView());
        }

        this.applySelectedEditableFilter(false);
      }
    });
    this.subscriptions.push(defaultFilterSub);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }
  private patchSavedFiltersForm(filterView: ProjectFilterView, editableFilter: LightweightProjectFilterView): void {
    this.savedFiltersForm.patchValue({
      filterViewName: filterView.filterViewName,
      flagType: editableFilter.flagTypeIds ?? [],
      office: editableFilter.officeIds ?? [],
      department: editableFilter.departmentIds ?? [],
      ownerClientType: editableFilter.ownerClientTypeIds ?? [],
      pm1Department: editableFilter.projectManager1DepartmentCodes ?? [],
      pm1Office: editableFilter.projectManager1OfficeCodes ?? [],
      pm1Employee: editableFilter.projectManager1EmployeeNumbers ?? [],
      pm2Employee: editableFilter.projectManager2EmployeeNumbers ?? [],
      taskManagerEmployee: editableFilter.taskManagerEmployeeNumbers ?? [],
      projectAccountantEmployee: editableFilter.projectAccountantEmployeeNumbers ?? [],
      isActiveProjectStatus: editableFilter.isActiveProjectStatus,
      isCompletedProjectStatus: editableFilter.isCompletedProjectStatus,
      isDesignBuild: editableFilter.isDesignBuild,
      isDefault: filterView.isDefault
    });
      this.savedFiltersForm.markAsPristine();
  }
  private loadFilterViews(): void {
    const allFilterViewsSub = this.filterViewService.getAllFilterViews().pipe(finalize(() => {
      this.subscriptions.push(allFilterViewsSub);
    })).subscribe({
      next: (result: ProjectFilterViewData) => {
        this.filterViews = result.filterViews;

        if (
          result.defaultFilterView &&
          this._mainService.pageFilteredByBillingProjectId == null &&
          this._mainService.pageFilteredByItemKey == null &&
          this._mainService.pageFilteredByProjectKey == null
        ) {
          this.selectedFilterView = result.defaultFilterView;
          this.editableFilter = new LightweightProjectFilterView().mapTo(result.defaultFilterView);
          this.patchSavedFiltersForm(this.selectedFilterView, this.editableFilter);
          this.savedFiltersForm.markAsPristine();
          this.applySelectedEditableFilter(false);
        }
      },
      error: (error: any) => {
        console.log(error);
      }
    });
  }

  //saves updates to an existing saved filter and applies
  public saveApplyFilterView(): void {
    if (!this.selectedFilterView?.filterViewKey) {
      return;
    }

    this.savedFiltersForm.markAllAsTouched();

    if (this.savedFiltersForm.invalid) {
      return;
    }

    if (!this.savedFiltersForm.dirty) {
      this.applySelectedEditableFilter(false);
      return;
    }
    const trimmedName = this.savedFiltersForm.get(this.FILTER_VIEW_NAME_FORM_CONTROL_NAME)?.value?.trim();

    const updatedFilterView = new LightweightProjectFilterView();
    updatedFilterView.filterViewKey = this.selectedFilterView.filterViewKey;
    updatedFilterView.filterViewName = trimmedName;
    updatedFilterView.isDefault = this.savedFiltersForm.get(this.DEFAULT_FILTER_FORM_CONTROL_NAME)?.value ?? false;

    updatedFilterView.flagTypeIds = this.savedFiltersForm.get(this.FLAG_TYPE_FORM_CONTROL_NAME)?.value ?? [];
    updatedFilterView.officeIds = this.savedFiltersForm.get(this.OFFICE_FORM_CONTROL_NAME)?.value ?? [];
    updatedFilterView.departmentIds = this.savedFiltersForm.get(this.DEPARTMENT_FORM_CONTROL_NAME)?.value ?? [];
    updatedFilterView.ownerClientTypeIds = this.savedFiltersForm.get(this.OWNER_CLIENT_TYPE_FORM_CONTROL_NAME)?.value ?? [];
    updatedFilterView.projectManager1DepartmentCodes = this.savedFiltersForm.get(this.PM1_DEPARTMENT_FORM_CONTROL_NAME)?.value ?? [];
    updatedFilterView.projectManager1OfficeCodes = this.savedFiltersForm.get(this.PM1_OFFICE_FORM_CONTROL_NAME)?.value ?? [];
    updatedFilterView.projectManager1EmployeeNumbers = this.savedFiltersForm.get(this.PM1_EMPLOYEE_FORM_CONTROL_NAME)?.value ?? [];
    updatedFilterView.projectManager2EmployeeNumbers = this.savedFiltersForm.get(this.PM2_EMPLOYEE_FORM_CONTROL_NAME)?.value ?? [];
    updatedFilterView.taskManagerEmployeeNumbers = this.savedFiltersForm.get(this.TASK_MANAGER_EMPLOYEE_FORM_CONTROL_NAME)?.value ?? [];
    updatedFilterView.projectAccountantEmployeeNumbers = this.savedFiltersForm.get(this.PROJECT_ACCOUNTANT_EMPLOYEE_FORM_CONTROL_NAME)?.value ?? [];
    updatedFilterView.isActiveProjectStatus = this.savedFiltersForm.get(this.ACTIVE_STATUS_FORM_CONTROL_NAME)?.value ?? false;
    updatedFilterView.isCompletedProjectStatus = this.savedFiltersForm.get(this.COMPLETED_STATUS_FORM_CONTROL_NAME)?.value ?? false;
    updatedFilterView.isDesignBuild = this.savedFiltersForm.get(this.DESIGN_BUILD_FORM_CONTROL_NAME)?.value ?? false;

    this.isFilterViewSaveInProgress = true;

    const updateFilterViewSub = this.filterViewService.updateFilterView(updatedFilterView).pipe(
      finalize(() => {
        this.subscriptions.push(updateFilterViewSub);
      })
    ).subscribe({
      next: () => {
        this.selectedFilterView.filterViewName = trimmedName;
        this.selectedFilterView.isDefault = updatedFilterView.isDefault;
        this.editableFilter = updatedFilterView;

        const index = this.filterViews.findIndex(x => x.filterViewKey === updatedFilterView.filterViewKey);

        if (index === -1) {
          this.filterViews.push(updatedFilterView);
        } else {
          this.filterViews[index] = updatedFilterView;
        }

        this.applySelectedEditableFilter(false);
        this.savedFiltersForm.markAsPristine();

        this.isFilterViewSaveInProgress = false;
        this.notificationService.showSuccess('Filter view has been updated and applied');
      },
      error: (error: any) => {
        this.isFilterViewSaveInProgress = false;

        if (error.status === 400 && error.error != null) {
          this.notificationService.showError(`${error.error}`);
        } else {
          this.notificationService.showError('Update of filter view failed due to an unexpected error. Please refresh the page and try again.');
        }
      }
    });
  }

  public toggleMenu(e: Event) {
    e.stopPropagation();
    this.isSavedFiltersMenuShowing = !this.isSavedFiltersMenuShowing;
    this.setMenuVisibility();
  }

  public deleteFilter(): void {
    if (this.selectedFilterView?.filterViewKey != null) {
      this.isFilterViewSaveInProgress = true;
      const deleteFilterViewSub = this.filterViewService.deleteFilterView(this.selectedFilterView.filterViewKey).pipe(finalize(() => {
        this.subscriptions.push(deleteFilterViewSub);
      })).subscribe({
        next: () => {
          this.filterViews = this.filterViews.filter(x => x.filterViewKey != this.selectedFilterView?.filterViewKey);
          this.selectedFilterView = new ProjectFilterView();
          this.editableFilter = new LightweightProjectFilterView();
          this.toggleMenuVisibility(true);
          this.isFilterViewSaveInProgress = false;

        this.notificationService.showSuccess("Filter view has been deleted");
        },
        error: () => {
          this.isFilterViewSaveInProgress = false;
        this.notificationService.showError("Deletion of filter view failed due to an unexpected error.  Please refresh the page and try again.");
      }});
    }

    this.toggleMenuVisibility(true);
  }

  public toggleMenuVisibility(closeMenu: boolean): void {
    this.closeMenu(closeMenu);
    this.setMenuVisibility();
  }

  // Applies the currently selected/edited filter to the page
  private applySelectedEditableFilter(closeMenuAfterApply: boolean): void {
    if (this.selectedFilterView?.filterViewKey == null) {
      this._mainService.isFilterApplied.next(false);
      return;
    }

    const lightweightSelectedFilterView = new LightweightProjectFilterView();
    lightweightSelectedFilterView.filterViewKey = this.selectedFilterView.filterViewKey;
    lightweightSelectedFilterView.filterViewName = this.savedFiltersForm.get(this.FILTER_VIEW_NAME_FORM_CONTROL_NAME)?.value ?? this.selectedFilterView.filterViewName;
    lightweightSelectedFilterView.isDefault = this.savedFiltersForm.get(this.DEFAULT_FILTER_FORM_CONTROL_NAME)?.value ?? false;

    lightweightSelectedFilterView.flagTypeIds = this.savedFiltersForm.get(this.FLAG_TYPE_FORM_CONTROL_NAME)?.value ?? [];
    lightweightSelectedFilterView.officeIds = this.savedFiltersForm.get(this.OFFICE_FORM_CONTROL_NAME)?.value ?? [];
    lightweightSelectedFilterView.departmentIds = this.savedFiltersForm.get(this.DEPARTMENT_FORM_CONTROL_NAME)?.value ?? [];
    lightweightSelectedFilterView.ownerClientTypeIds = this.savedFiltersForm.get(this.OWNER_CLIENT_TYPE_FORM_CONTROL_NAME)?.value ?? [];
    lightweightSelectedFilterView.projectManager1DepartmentCodes = this.savedFiltersForm.get(this.PM1_DEPARTMENT_FORM_CONTROL_NAME)?.value ?? [];
    lightweightSelectedFilterView.projectManager1OfficeCodes = this.savedFiltersForm.get(this.PM1_OFFICE_FORM_CONTROL_NAME)?.value ?? [];
    lightweightSelectedFilterView.projectManager1EmployeeNumbers = this.savedFiltersForm.get(this.PM1_EMPLOYEE_FORM_CONTROL_NAME)?.value ?? [];
    lightweightSelectedFilterView.projectManager2EmployeeNumbers = this.savedFiltersForm.get(this.PM2_EMPLOYEE_FORM_CONTROL_NAME)?.value ?? [];
    lightweightSelectedFilterView.taskManagerEmployeeNumbers = this.savedFiltersForm.get(this.TASK_MANAGER_EMPLOYEE_FORM_CONTROL_NAME)?.value ?? [];
    lightweightSelectedFilterView.projectAccountantEmployeeNumbers = this.savedFiltersForm.get(this.PROJECT_ACCOUNTANT_EMPLOYEE_FORM_CONTROL_NAME)?.value ?? [];
    lightweightSelectedFilterView.isDesignBuild = this.savedFiltersForm.get(this.DESIGN_BUILD_FORM_CONTROL_NAME)?.value ?? false;
    lightweightSelectedFilterView.isActiveProjectStatus = this.savedFiltersForm.get(this.ACTIVE_STATUS_FORM_CONTROL_NAME)?.value ?? false;
    lightweightSelectedFilterView.isCompletedProjectStatus = this.savedFiltersForm.get(this.COMPLETED_STATUS_FORM_CONTROL_NAME)?.value ?? false;

    this.filterViewService.filterViewApplied$.next(lightweightSelectedFilterView);
    this._mainService.isFilterApplied.next(true);

    if (closeMenuAfterApply) {
      this.toggleMenuVisibility(true);
    }
  }
  private closeMenu(close: boolean): void {
    this.isSavedFiltersMenuShowing = !close;
  }

  private setMenuVisibility(): void {
    if (this.isSavedFiltersMenuShowing) {
      //set service variable for menu shown
      this._mainService.setIsMenuVisible(FilterMenu.Saved);
    } else {
      this._mainService.setIsMenuVisible(FilterMenu.None);
    }
  }
  public setSelectedFilterView(selectedFilterViewKey: string): void {
    this.isSavedFilterSelected = true;

    if (selectedFilterViewKey != null && selectedFilterViewKey != "-1") {
      this.shouldUseScrollableOverflow = true;
      const getFilterViewSub = this.filterViewService.getFilterView(selectedFilterViewKey).pipe(finalize(() => {
        this.subscriptions.push(getFilterViewSub);
      })).subscribe({
        next: (result: ProjectFilterView) => {
          this.selectedFilterView = result;
          this.editableFilter = new LightweightProjectFilterView().mapTo(result);

          this.savedFiltersForm.patchValue({
            filterViewName: result.filterViewName,
            flagType: this.editableFilter.flagTypeIds ?? [],
            office: this.editableFilter.officeIds ?? [],
            department: this.editableFilter.departmentIds ?? [],
            ownerClientType: this.editableFilter.ownerClientTypeIds ?? [],
            pm1Department: this.editableFilter.projectManager1DepartmentCodes ?? [],
            pm1Office: this.editableFilter.projectManager1OfficeCodes ?? [],
            pm1Employee: this.editableFilter.projectManager1EmployeeNumbers ?? [],
            pm2Employee: this.editableFilter.projectManager2EmployeeNumbers ?? [],
            taskManagerEmployee: this.editableFilter.taskManagerEmployeeNumbers ?? [],
            projectAccountantEmployee: this.editableFilter.projectAccountantEmployeeNumbers ?? [],
            isActiveProjectStatus: this.editableFilter.isActiveProjectStatus,
            isCompletedProjectStatus: this.editableFilter.isCompletedProjectStatus,
            isDesignBuild: this.editableFilter.isDesignBuild,
            isDefault: this.selectedFilterView.isDefault
          });
          this.savedFiltersForm.markAsPristine();
        },
        error: (error: any) => {
          console.log(error);
        }
      });
    } else {
      this.shouldUseScrollableOverflow = false;
      this.selectedFilterView = new ProjectFilterView();
      this.editableFilter = new LightweightProjectFilterView();

      this.savedFiltersForm.reset({
        filterViewName: null,
        flagType: [],
        office: [],
        department: [],
        ownerClientType: [],
        pm1Department: [],
        pm1Office: [],
        pm1Employee: [],
        pm2Employee: [],
        taskManagerEmployee: [],
        projectAccountantEmployee: [],
        isActiveProjectStatus: false,
        isCompletedProjectStatus: false,
        isDesignBuild: false,
        isDefault: false
      });
      this.savedFiltersForm.markAsPristine();
    }
  }
}
